import { UAParser } from 'ua-parser-js';
import DOMPurify from 'dompurify';

export const checkShouldDisplaySameUrl = (): boolean => {
  return process.env.VUE_APP_DISPLAY_SAME_EULA_FOR_UQ === 'true';
};

export const isAndroid = (): boolean => {
  try {
    const parser = new UAParser();
    const result = parser.getResult().os.name || '';
    if (result.toLowerCase() === 'ios' || result.toLowerCase() === 'mac os') {
      return false;
    } else {
      return true;
    }
  } catch {
    return false;
  }
};

export const isIOS = (): boolean => {
  try {
    const parser = new UAParser();
    const result = parser.getResult().os.name || '';
    if (result.toLowerCase() === 'ios' || result.toLowerCase() === 'mac os') {
      return true;
    } else {
      return false;
    }
  } catch {
    return false;
  }
};

/**
 * Adds a DOMPurify hook to selectively allow certain 'onclick' attributes.
 * This hook checks if the attribute being sanitized is 'onclick'. If so,
 * it only keeps the attribute if its value contains either
 * 'window.gotoDetailsFromInjected' or 'window.startBrowser'. This helps
 * prevent unwanted or unsafe inline JavaScript, while allowing specific
 * trusted handlers.
 */
DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
  if (data.attrName === 'onclick') {
    data.keepAttr = data.attrValue.includes('window.gotoDetailsFromInjected') || data.attrValue.includes('window.startBrowser');
  }
});

/**
 * Extracts and sanitizes the inner HTML and inline styles from a given HTML string.
 *
 * This function parses the provided HTML string, extracts its inner HTML,
 * sanitizes it using DOMPurify (allowing the 'onclick' attribute), and collects
 * all inline `<style>` elements into a single `HTMLStyleElement` with the id 'page-only-styles'.
 *
 * @param html - The raw HTML string to be processed.
 * @returns An object containing:
 *   - `purifiedHtml`: The sanitized inner HTML as a string.
 *   - `styleEl`: An `HTMLStyleElement` containing all extracted inline CSS.
 */
export const extractAndPurifyHtml = (html: string): { purifiedHtml: string, styleEl: HTMLStyleElement } => {
  const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
  const innerHtml = parsedHtml.body?.innerHTML || '';
  const purifiedHtml = DOMPurify.sanitize(innerHtml, {
    ADD_ATTR: ['onclick']
  });
  const inlineCss = Array.from(parsedHtml.querySelectorAll('style'))
    .map(s => s.textContent || '')
    .join('\n');
  const styleEl = document.createElement('style');
  styleEl.id = 'page-only-styles';
  styleEl.textContent = inlineCss;
  return { purifiedHtml, styleEl };
};
